import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	assignAddressNumber,
	assignNumber,
	BGupdateDocument,
	editInfo,
	getData,
	getObjectLocations,
	getSearchedData,
	updateDocument,
	uploadDocument,
} from "./stepSeven.thunks";
import { IData } from "./stepSeven.types";
import { PAGINATION_COUNT_STEP_SEVEN } from "constant/index";

interface StepSevenState {
	id: number | null;
	data: IData;
	attachmentMode: boolean;
	attachmentList: number[];
	excludedObjects: number[];
	allChecked: boolean | null;
	allCheckedGlobal: boolean | null;

	tableMode: boolean;

	searchValue: string;
	searchedData: IData;
	searchMode: boolean;
	searchPending: boolean;

	lastPart: number | null;
	lastSearchedPart: number | null;
}

const initialState: StepSevenState = {
	id: null,
	data: {
		assigned: [],
		unassigned: [],
		repeatable: [],
		noLetter: [],
	},
	attachmentMode: false,
	// selection
	attachmentList: [],
	excludedObjects: [],
	allChecked: null,
	allCheckedGlobal: null,

	tableMode: true,
	// search
	searchValue: "",
	searchedData: {
		assigned: [],
		unassigned: [],
		repeatable: [],
		noLetter: [],
	},
	searchMode: false,
	searchPending: false,

	lastPart: 0,
	lastSearchedPart: 0,
};

const _resetPagination = (state: typeof initialState) => {
	console.log("_resetPagination");
	state.lastPart = 0;
	state.lastSearchedPart = 0;
	state.data = {
		assigned: [],
		unassigned: [],
		repeatable: [],
		noLetter: [],
	};
	state.searchedData = {
		assigned: [],
		unassigned: [],
		repeatable: [],
		noLetter: [],
	};
};

const stepSevenSlice = createSlice({
	name: "stepSevenSlice",
	initialState,
	reducers: {
		changeId(state, action: PayloadAction<number>) {
			state.id = action.payload;
			state.data = {
				assigned: [],
				noLetter: [],
				repeatable: [],
				unassigned: [],
			};
		},
		changeAttachmentMode(state, action: PayloadAction<boolean>) {
			state.attachmentMode = action.payload;
		},
		// 1
		addToAttachmentList(state, action: PayloadAction<number>) {
			if (!state.attachmentList.includes(action.payload)) {
				state.attachmentList.push(action.payload);
			}
		},
		// 2
		removeFromAttachmentList(state, action: PayloadAction<number>) {
			state.allChecked = null;
			state.attachmentList = state.attachmentList.filter((objId) => objId !== action.payload);
			if (state.allCheckedGlobal) {
				state.excludedObjects.push(action.payload);
			}
		},
		changeChooseAll(state, action: PayloadAction<boolean | null>) {
			state.allChecked = action.payload;
			if (action.payload !== null && !action.payload) state.attachmentList = [];
			if (action.payload) {
				for (let i = 1; i <= 2; i++) {
					const localList =
						i === 1
							? (state.searchMode ? state.searchedData : state.data).noLetter
							: (state.searchMode ? state.searchedData : state.data).repeatable;

					localList.forEach((object) => {
						if (!state.attachmentList.includes(object.id)) {
							state.attachmentList.push(object.id);
						}
					});
				}
			}
		},
		changeChooseAllGlobal(state, action: PayloadAction<boolean | null>) {
			if (state.searchMode) return;
			state.excludedObjects = [];
			state.allCheckedGlobal = action.payload;
		},
		clearAttachmentList(state) {
			state.allChecked = null;
			state.attachmentList = [];
		},
		clearGlobalChecked(state) {
			state.excludedObjects = [];
			state.allCheckedGlobal = null;
		},
		changeTableMode(state, action: PayloadAction<boolean>) {
			state.tableMode = action.payload;
		},
		changeSearchValue(state, action: PayloadAction<string>) {
			state.searchValue = action.payload;
			state.lastSearchedPart = 0;
			state.searchedData = {
				assigned: [],
				unassigned: [],
				repeatable: [],
				noLetter: [],
			};
			if (!state.searchValue) {
				state.searchMode = false;
				state.searchPending = false;
				state.lastPart = 0;
				state.data = {
					assigned: [],
					unassigned: [],
					repeatable: [],
					noLetter: [],
				};
			} else {
				state.searchMode = false;
				state.searchMode = true;
				state.searchPending = true;
			}
		},
		resetPagination(state) {
			_resetPagination(state);
		},
		resetStep_7: (state) => ({ ...initialState, tableMode: state.tableMode }),
	},
	extraReducers: (builder) => {
		builder.addCase(getData.fulfilled, (state, action) => {
			state.data.assigned.push(...action.payload.assigned);
			state.data.unassigned.push(...action.payload.unassigned);
			state.data.repeatable.push(...action.payload.repeatable);
			state.data.noLetter.push(...action.payload.noLetter);

			let arrLength: any[] = [];
			arrLength = arrLength.concat(
				action.payload.assigned,
				action.payload.unassigned,
				action.payload.repeatable,
				action.payload.noLetter,
			);
			if (arrLength.length && state.lastPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SEVEN)
				state.lastPart++;
			else {
				state.lastPart = null;
			}
		});
		builder.addCase(getSearchedData.fulfilled, (state, action) => {
			state.searchedData.assigned.push(...action.payload.assigned);
			state.searchedData.unassigned.push(...action.payload.unassigned);
			state.searchedData.repeatable.push(...action.payload.repeatable);
			state.searchedData.noLetter.push(...action.payload.noLetter);
			state.searchPending = false;

			let arrLength: any[] = [];
			arrLength = arrLength.concat(
				action.payload.assigned,
				action.payload.unassigned,
				action.payload.repeatable,
				action.payload.noLetter,
			);
			if (arrLength.length && state.lastSearchedPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SEVEN)
				state.lastSearchedPart++;
			else {
				state.lastSearchedPart = null;
			}
		});
		builder.addCase(
			uploadDocument.fulfilled,
			(
				state,
				action: PayloadAction<{
					response: IData;
					objectQuantity: number;
					handler?: boolean;
					filter?: boolean;
					reload?: boolean;
				}>,
			) => {
				if (!action.payload.handler) return;

				const response = action.payload.response;

				state.searchMode = false;
				state.searchPending = false;
				state.searchValue = "";

				if (action.payload.objectQuantity > 1 && action.payload.reload) {
					_resetPagination(state);
					state.data.assigned.push(...response.assigned);
					state.data.unassigned.push(...response.unassigned);
					state.data.repeatable.push(...response.repeatable);
					state.data.noLetter.push(...response.noLetter);

					let arrLength: any[] = [];
					arrLength = arrLength.concat(response.assigned, response.unassigned, response.repeatable, response.noLetter);
					if (arrLength.length && state.lastPart !== null && arrLength.length === PAGINATION_COUNT_STEP_SEVEN)
						state.lastPart++;
					else {
						state.lastPart = null;
					}
					return;
				}

				state.data.assigned.push(...response.assigned);
				state.data.unassigned.push(...response.unassigned);

				if (action.payload.filter) {
					let attachmentIdList: number[] = [];
					response.unassigned.map((object) => attachmentIdList.push(object.id));
					response.assigned.map((object) => attachmentIdList.push(object.id));

					state.data.noLetter = state.data.noLetter.filter((object) => !attachmentIdList.includes(object.id));
					state.data.repeatable = state.data.repeatable.filter((object) => !attachmentIdList.includes(object.id));

					attachmentIdList = [];
					response.assigned.map((object) => attachmentIdList.push(object.id));
					response.repeatable.map((object) => attachmentIdList.push(object.id));
					state.data.repeatable = state.data.repeatable.filter((object) => !attachmentIdList.includes(object.id));
					state.data.repeatable.push(...response.repeatable);

					state.allChecked = null;
					state.allCheckedGlobal = null;
					state.attachmentList = [];
					state.excludedObjects = [];
				}
			},
		);
		builder.addCase(updateDocument.fulfilled, (state, action) => {
			const newObject = action.payload.assigned?.[0];

			const localData = state.searchMode ? state.searchedData : state.data;
			localData.unassigned.filter((object) => object.id !== newObject.id);
			localData.repeatable.filter((object) => object.id !== newObject.id);
			localData.assigned.push(newObject);
		});
		builder.addCase(assignNumber.fulfilled, (state, action: PayloadAction<{ response: IData; handler: boolean }>) => {
			if (!action.payload.handler) return;
			const newObject = action.payload.response.assigned?.[0];
			if (action.payload.response.assigned.length) {
				state.data.unassigned = state.data.unassigned.filter((object) => newObject.id !== object.id);
				state.data.repeatable = state.data.repeatable.filter((object) => newObject.id !== object.id);
				state.data.assigned.push(newObject);
			}
			if (action.payload.response.noLetter.length) {
				const objectIndex = state.data.noLetter.findIndex(
					(object) => object.id === action.payload.response.noLetter[0].id,
				);
				if (objectIndex >= 0) state.data.noLetter[objectIndex] = action.payload.response.noLetter[0];
			}
			if (action.payload.response.repeatable.length) {
				const objectIndex = state.data.repeatable.findIndex(
					(object) => object.id === action.payload.response.repeatable[0].id,
				);
				if (objectIndex >= 0) state.data.repeatable[objectIndex] = action.payload.response.repeatable?.[0];
			}

			if (state.searchMode) {
				if (action.payload.response.assigned.length) {
					state.searchedData.unassigned = state.searchedData.unassigned.filter((object) => newObject.id !== object.id);
					state.searchedData.repeatable = state.searchedData.repeatable.filter((object) => newObject.id !== object.id);
					state.searchedData.assigned.push(newObject);
				}
				if (action.payload.response.noLetter.length) {
					const objectIndex = state.searchedData.noLetter.findIndex(
						(object) => object.id === action.payload.response.noLetter[0].id,
					);
					if (objectIndex >= 0) state.searchedData.noLetter[objectIndex] = action.payload.response.noLetter[0];
				}

				if (action.payload.response.repeatable.length) {
					const objectIndex = state.searchedData.repeatable.findIndex(
						(object) => object.id === action.payload.response.repeatable[0].id,
					);
					if (objectIndex >= 0) state.searchedData.repeatable[objectIndex] = action.payload.response.repeatable?.[0];
				}
			}
		});
		builder.addCase(assignAddressNumber.fulfilled, (state, action) => {
			const addresIndex = action.payload.object.locations.findIndex(
				(address) => address.id === action.payload.response.id,
			);

			for (let key in state.data) {
				const objectIndex = state.data[key as keyof typeof state.data].findIndex(
					(object) => object.id === action.payload.object.id,
				);
				if (objectIndex >= 0) {
					state.data[key as keyof typeof state.data][objectIndex].locations[addresIndex] = action.payload.response;
					return;
				}
			}

			if (state.searchMode) {
				for (let key in state.searchedData) {
					const objectIndex = state.searchedData[key as keyof typeof state.searchedData].findIndex(
						(object) => object.id === action.payload.object.id,
					);
					if (objectIndex >= 0) {
						state.searchedData[key as keyof typeof state.searchedData][objectIndex].locations[addresIndex] =
							action.payload.response;
						return;
					}
				}
			}
		});
		builder.addCase(getObjectLocations.fulfilled, (state, action) => {
			for (let key in state.data) {
				const objectIndex = state.data[key as keyof typeof state.data].findIndex(
					(object) => object.id === action.payload.object.id,
				);
				if (objectIndex >= 0) {
					state.data[key as keyof typeof state.data][objectIndex].locations = action.payload.response;
					return;
				}
			}

			if (state.searchMode) {
			}
		});
		builder.addCase(editInfo.fulfilled, (state, action: PayloadAction<{ response: IData; handler: boolean }>) => {
			if (!action.payload.handler) return;
			const newObject = action.payload.response.assigned[0];
			let objectIndex: number = state.data.assigned.findIndex((object) => object.id === newObject.id);
			if (objectIndex >= 0) state.data.assigned[objectIndex] = newObject;

			if (state.searchMode) {
				let objectIndex: number = state.searchedData.assigned.findIndex((object) => object.id === newObject.id);
				if (objectIndex >= 0) state.searchedData.assigned[objectIndex] = newObject;
			}
		});
		builder.addCase(BGupdateDocument.fulfilled, (state, action: PayloadAction<IData>) => {
			const newObject = action.payload.assigned[0];
			let objectIndex: number = state.data.assigned.findIndex((object) => object.id === newObject.id);
			if (objectIndex >= 0) state.data.assigned[objectIndex] = newObject;

			if (state.searchMode) {
				let objectIndex: number = state.searchedData.assigned.findIndex((object) => object.id === newObject.id);
				if (objectIndex >= 0) state.searchedData.assigned[objectIndex] = newObject;
			}
		});
	},
});

export const {
	changeId,
	changeAttachmentMode,
	addToAttachmentList,
	removeFromAttachmentList,
	changeTableMode,
	changeSearchValue,
	changeChooseAll,
	clearAttachmentList,
	clearGlobalChecked,
	changeChooseAllGlobal,
	resetPagination,
	resetStep_7,
} = stepSevenSlice.actions;
export default stepSevenSlice.reducer;
